import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAIWorkspace } from "@/hooks/useAIWorkspace";
import { CopilotChat } from "@/components/copilot/CopilotChat";
import { CopilotPreview } from "@/components/copilot/CopilotPreview";
import { NextStepCard } from "@/components/copilot/NextStep";
import { useSessionJob, useJourneyActions, jobIdOf } from "@/components/copilot/useJourney";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { toast } from "sonner";
import { Bot, Eye, PanelRightClose, Plus, X, ArrowLeft } from "lucide-react";

const DESKTOP = "(min-width: 768px)";
function useDesktop() {
  const [ok, setOk] = useState(() => window.matchMedia(DESKTOP).matches);
  useEffect(() => {
    const m = window.matchMedia(DESKTOP);
    const h = (e) => setOk(e.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  return ok;
}

export default function Copilot() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { sessionId } = useParams();
  const openChat = useAIWorkspace();
  const role = user?.role === "candidate" ? "candidate" : "recruiter";
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [drafts, setDrafts] = useState({});
  const draftKey = sessionId || "new";
  const input = drafts[draftKey] || "";
  const setInput = (value) => setDrafts((d) => ({ ...d, [draftKey]: value }));
  const routeRef = useRef(sessionId);
  routeRef.current = sessionId;
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [preview, setPreview] = useState(false);
  const [dragging, setDragging] = useState(false);
  const desktop = useDesktop();
  const [error, setError] = useState("");
  const busy = sending || deciding || session?.status === "running";
  const loadTabs = () => api.get("/ai/sessions").then(({ data }) => setSessions(data)).catch(() => {});

  useEffect(() => {
    let alive = true, timer;
    setSession(null); setSending(false); setDeciding(false); setPreview(false); setError("");
    loadTabs();
    if (!sessionId) return;
    const key = `hs_ai_pending_${user.id}_${sessionId}`;
    const load = async () => {
      try {
        const raw = sessionStorage.getItem(key);
        const { data } = raw ? await api.put(`/ai/sessions/${sessionId}`, JSON.parse(raw)) : await api.get(`/ai/sessions/${sessionId}`);
        sessionStorage.removeItem(key);
        if (!alive) return;
        setSession(data); setError(""); loadTabs();
        if (data.status === "running") timer = setTimeout(load, 1800);
      } catch (e) { if (alive) setError(e.response?.data?.detail || "Could not open this conversation."); }
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [sessionId, user.id]);

  // Open the preview by itself whenever the copilot creates or updates something (desktop only, so mobile keeps the questions visible).
  const previewUrl = session?.preview_url;
  const previewRev = session?.preview_rev || 0;
  const refinementId = session?.refinement?.status === "ready" ? session.refinement.id : null;
  const hasArtifact = previewRev > 0 || !!refinementId || !!session?.job_id;
  useEffect(() => { if (desktop && hasArtifact) setPreview(true); }, [session?.id, previewRev, refinementId, hasArtifact, desktop]);

  // Poll a submitted turn independently from the route's initial load.
  useEffect(() => {
    if (!sending || !sessionId) return;
    let alive = true;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/ai/sessions/${sessionId}`);
        if (alive) { setSession(data); if (data.status !== "running") { setSending(false); loadTabs(); } }
      } catch { if (alive) { setSending(false); setError("Connection interrupted. Reopen this tab to resume."); } }
    }, 1800);
    return () => { alive = false; clearInterval(timer); };
  }, [sending, sessionId]);

  const send = async (text, action) => {
    const prompt = (text ?? input).trim();
    if (!prompt || busy) return;
    if (!sessionId) { setInput(""); openChat(prompt, { mode: role === "candidate" ? "Career Copilot" : "Copilot", previewUrl: role === "candidate" ? "/candidate/profile" : "/recruiter/jobs" }); return; }
    setSending(true); setError("");
    try {
      const { data } = await api.post(`/ai/sessions/${sessionId}/messages`, { prompt, request_id: crypto.randomUUID(), ...(action ? { action } : {}) });
      if (routeRef.current === sessionId) setSession(data);
      if (!action) setInput("");
    } catch (e) { if (routeRef.current === sessionId) setSending(false); toast.error(e.response?.data?.detail || "Could not send. Your text is still here."); }
  };
  const close = async (id) => {
    try {
      await api.post(`/ai/sessions/${id}/close`);
      const next = sessions.filter((s) => s.id !== id);
      setSessions(next);
      if (id === sessionId) nav(next.length ? `/copilot/${next[0].id}` : "/copilot");
    } catch { toast.error("Could not close this tab"); }
  };
  const defaultHome = user.role === "super_admin" ? "/superadmin" : role === "candidate" ? "/candidate" : "/recruiter";

  // The role this conversation is building, with its lifecycle — answers "what now?".
  const { job, refresh } = useSessionJob(role === "candidate" ? null : session?.job_id || jobIdOf(previewUrl), previewRev, busy);
  const actions = useJourneyActions(job, sessionId, send, refresh, busy);
  const last = session?.messages?.at(-1);
  const showNext = !!job && !!sessionId && !busy && session?.mode !== "Question Refinement" && last?.role === "assistant" && !last.questions?.length;

  const chat = <CopilotChat role={role} sessionId={sessionId} session={session} error={error} busy={busy} input={input} setInput={setInput} send={send}
    previewCta={hasArtifact && !preview} onOpenPreview={() => setPreview(true)} footer={showNext ? <NextStepCard job={job} actions={actions} previewOpen={preview} previewUrl={previewUrl} /> : null} />;
  const pane = <CopilotPreview session={session} previewUrl={previewUrl} busy={busy} dragging={dragging} job={job} onClose={() => setPreview(false)}
    onDecisionBusy={(value) => { if (routeRef.current === session?.id) setDeciding(value); }}
    onSession={(data) => { if (routeRef.current === data.id) { setSession(data); loadTabs(); } }} />;

  return (
    <div className="ai-workspace" data-testid="copilot-page">
      <div className="ai-session-tabs" data-testid="ai-session-tabs">
        <button onClick={() => nav(defaultHome)} aria-label="Back to home" data-testid="ai-home" className="ai-icon-button shrink-0"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex flex-1 min-w-0 gap-1 overflow-x-auto no-scrollbar" role="tablist" aria-label="AI conversations">
          {sessions.map((s) => <div key={s.id} className={`ai-session-tab ${sessionId === s.id ? "is-active" : ""}`}>
            <button role="tab" aria-selected={sessionId === s.id} onClick={() => nav(`/copilot/${s.id}`)} data-testid={`ai-session-${s.id}`} title={s.title} className="flex min-w-0 items-center gap-2 py-1.5 pl-3 pr-1 text-sm">
              <span className={`h-2 w-2 rounded-full shrink-0 ${s.status === "running" ? "bg-primary animate-pulse" : "bg-primary/50"}`} /><span className="truncate">{s.title}</span>
            </button>
            <button onClick={() => close(s.id)} aria-label={`Close ${s.title}`} data-testid={`ai-close-${s.id}`} className="ai-icon-button shrink-0"><X className="h-3 w-3" /></button>
          </div>)}
          {!sessionId && <span className="px-3 py-1.5 text-sm text-primary shrink-0" data-testid="ai-new-tab">New conversation</span>}
        </div>
        <button onClick={() => nav("/copilot")} aria-label="New conversation" title="New conversation" data-testid="ai-new-chat" className="ai-icon-button shrink-0"><Plus className="h-4 w-4" /></button>
        <span className="hidden xl:inline-flex items-center gap-1.5 shrink-0 max-w-[180px] text-xs text-muted-foreground" data-testid="ai-session-mode"><Bot className="h-3.5 w-3.5 text-primary shrink-0" /><span className="truncate">{session?.mode || (role === "candidate" ? "Career Copilot" : "Hiring Copilot")}</span></span>
        <button onClick={() => setPreview((p) => !p)} aria-expanded={preview} data-testid="ai-preview-toggle" className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 h-8 text-xs hover:bg-muted transition-colors">
          {preview ? <PanelRightClose className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{preview ? (desktop ? "Hide preview" : "Back to chat") : "Preview"}
        </button>
      </div>
      <div className="ai-workspace-body">
        {preview && desktop ? (
          <ResizablePanelGroup direction="horizontal" autoSaveId="hs-copilot-split" data-testid="ai-split">
            <ResizablePanel id="ai-chat" order={1} defaultSize={42} minSize={25} className="flex min-w-0 min-h-0">{chat}</ResizablePanel>
            <ResizableHandle withHandle onDragging={setDragging} aria-label="Resize chat and preview" data-testid="ai-split-handle"
              className="w-1.5 bg-border hover:bg-primary/40 data-[resize-handle-state=drag]:bg-primary/60 transition-colors" />
            <ResizablePanel id="ai-preview" order={2} defaultSize={58} minSize={28} className="flex min-w-0 min-h-0">{pane}</ResizablePanel>
          </ResizablePanelGroup>
        ) : preview ? pane : chat}
      </div>
    </div>
  );
}
